import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { Cacheable } from 'ngx-cacheable';

import { RemoteApiService, Station } from '../core';
import { MarketFormatPipe } from '../shared/market-format.pipe';

export interface SearchFilterAggregate {
    name: string;
    aggregate: any[];
}

@Injectable()
export class SearchFiltersService {

    constructor(private remoteApi: RemoteApiService, private marketPipe: MarketFormatPipe) {
    }

    @Cacheable({
        shouldCacheDecider: (response: Station[]) => response.length > 0
    })
    loadAllStations(): Observable<Station[]> {
        return this.loadAllStationsNoCache();
    }

    loadAllStationsNoCache(): Observable<Station[]> {
        const _stations: BehaviorSubject<Station[]> = <BehaviorSubject<Station[]>>new BehaviorSubject([]);
        const stations = _stations.asObservable();

        const parameters: any = {};
        this.remoteApi.getStream<Station[]>('/../v2/station', parameters)
            .subscribe((data: Station[]) => {
                const stationList: Station[] = data.filter(station => {
                    return station != null && (
                        (station.name != null && station.name.trim() !== '')
                        || (station.frequency != null && station.frequency.trim() !== '')
                    );
                });

                stationList.sort((o1, o2) => {
                    return `${o1.name} ${o1.frequency}`.toLowerCase()
                        .localeCompare(`${o2.name} ${o2.frequency}`.toLowerCase());
                });

                _stations.next(stationList);

            }, error => {
                _stations.error(error);
            }, () => {
                _stations.complete();
            });

        return stations;
    }

    @Cacheable({
        shouldCacheDecider: (response: SearchFilterAggregate[]) => response.length > 0
    })
    loadAllMarkets(): Observable<SearchFilterAggregate[]> {
        return this.loadAllMarketsNoCache();
    }

    loadAllMarketsNoCache(): Observable<SearchFilterAggregate[]> {

        const _markets: BehaviorSubject<SearchFilterAggregate[]> = <BehaviorSubject<SearchFilterAggregate[]>>new BehaviorSubject([]);
        const markets = _markets.asObservable();

        const parameters: any = {};
        this.remoteApi.getStream<Station[]>('/../v2/station', parameters)
            .subscribe((data: Station[]) => {
                const aggregatedList: SearchFilterAggregate[] = data.reduce((combiner, transformer) => {
                    const value = combiner.find((combine) => combine.name === this.marketPipe.transform(transformer.market));

                    if (value) {
                        if (!value.aggregate.includes(transformer.market)) {
                            value.aggregate.push(transformer.market);
                        }
                    } else {
                        combiner.push({ name: this.marketPipe.transform(transformer.market), aggregate: [transformer.market] });
                    }

                    return combiner;
                }, []);

                aggregatedList.sort((o1, o2) => {
                    return o1.name.toLowerCase()
                        .localeCompare(o2.name.toLowerCase());
                });

                _markets.next(aggregatedList);
            }, error => {
                _markets.error(error);
            }, () => {
                _markets.complete();
            });

        return markets;
    }

    @Cacheable({
        shouldCacheDecider: (response: SearchFilterAggregate[]) => response.length > 0
    })
    loadAllFormats(): Observable<SearchFilterAggregate[]> {
        return this.loadAllFormatsNoCache();
    }

    loadAllFormatsNoCache() {
        const _formats: BehaviorSubject<SearchFilterAggregate[]> = <BehaviorSubject<SearchFilterAggregate[]>>new BehaviorSubject([]);
        const formats = _formats.asObservable();

        const parameters: any = {};
        this.remoteApi.getStream<Station[]>('/../v2/station', parameters)
            .subscribe((data: Station[]) => {
                const aggregatedList: SearchFilterAggregate[] = data.reduce((combiner, transformer) => {
                    const value = combiner.find((combine) => combine.name === transformer.format);

                    if (value) {
                        if (!value.aggregate.includes(transformer.market)) {
                            value.aggregate.push(transformer.market);
                        }
                    } else {
                        combiner.push({ name: transformer.format, aggregate: [transformer.market] });
                    }

                    return combiner;
                }, []);

                aggregatedList.sort((o1, o2) => {
                    return o1.name.toLowerCase()
                        .localeCompare(o2.name.toLowerCase());
                });

                _formats.next(aggregatedList);
            }, error => {
                _formats.error(error);
            }, () => {
                _formats.complete();
            });

        return formats;
    }
}
